import { useState, useMemo, useEffect, useRef } from 'react'
import { pdf } from '@react-pdf/renderer'
import { useAuth } from '@/features/auth/AuthProvider'
import { useProductos, useDepositos, useTodasLasReglas, getStockDeposito } from '@/features/productos/hooks'
import { useConfirmarVenta, useEnviarRecibo, getCartTotal, getPrecioParaTipo } from '@/features/ventas/hooks'
import type { CartItem } from '@/features/ventas/hooks'
import { useClientes } from '@/features/clientes/hooks'
import { useCajaActual } from '@/features/caja/hooks'
import { useConfiguracion } from '@/features/configuracion/hooks'
import { TicketDocument } from './TicketPDF'
import { printTicket } from './printTicket'
import type { CajaSesion, Cliente, MetodoPago, Producto, PrecioRegla, Venta } from '@/types/database'
import { formatMoney } from '@/lib/utils'
import toast from 'react-hot-toast'
import {
  SearchIcon, PlusIcon, MinusIcon, CloseIcon,
  CheckIcon, BoxIcon, AlertTriangleIcon, CameraIcon, UsersIcon, WarehouseIcon, MailIcon, PrintIcon,
} from '@/components/shared/Icons'

const METODOS_BASE: { key: MetodoPago; label: string; kbd: string }[] = [
  { key: 'efectivo',      label: 'Efectivo',     kbd: 'F1' },
  { key: 'transferencia', label: 'Transferencia', kbd: 'F2' },
  { key: 'debito',        label: 'Débito',        kbd: 'F3' },
  { key: 'credito',       label: 'Crédito',       kbd: 'F4' },
]
const METODO_CC = { key: 'cuenta_corriente' as MetodoPago, label: 'Cta. Cte.', kbd: 'F5' }

const METODO_KBD: Record<string, MetodoPago> = {
  F1: 'efectivo', F2: 'transferencia', F3: 'debito', F4: 'credito', F5: 'cuenta_corriente',
}

interface NuevaVentaProps {
  // Cuando se usa embebida en CajaPage, recibe la sesión ya cargada
  embeddedCajaActual?: (CajaSesion & { profiles?: { nombre: string } }) | null
  embedded?: boolean
}

export default function NuevaVentaPage({ embeddedCajaActual, embedded = false }: NuevaVentaProps = {}) {
  const { profile } = useAuth()
  const { data: productos = [], isLoading } = useProductos()
  const { data: depositos = [] } = useDepositos()
  const { data: clientes = [] } = useClientes({ soloActivos: true })
  // Si está embebida en CajaPage, usa la sesión que le pasan (sin re-fetchear)
  const { data: cajaFetched } = useCajaActual(embedded ? undefined : profile?.id)
  const cajaActual = embedded ? embeddedCajaActual : cajaFetched
  const { data: reglas = [] } = useTodasLasReglas()
  const { data: config } = useConfiguracion()
  const confirmarVenta = useConfirmarVenta()

  const [ventaCompletada, setVentaCompletada] = useState<{ numero: number; ventaObj: Venta; clienteEmail: string | null } | null>(null)
  const [descargandoPDF, setDescargandoPDF] = useState(false)
  const enviarRecibo = useEnviarRecibo()

  function applyVolumeRule(producto: Producto, cantidad: number, tipo: 'minorista' | 'mayorista'): number {
    const base = getPrecioParaTipo(producto, tipo)
    if (tipo === 'mayorista') return base
    const productoReglas = reglas
      .filter((r: PrecioRegla) => r.producto_id === producto.id)
      .sort((a: PrecioRegla, b: PrecioRegla) => b.cantidad_minima - a.cantidad_minima)
    const match = productoReglas.find((r: PrecioRegla) => cantidad >= r.cantidad_minima)
    if (!match) return base
    if (match.tipo_precio === 'personalizado' && match.precio_especial != null) return match.precio_especial
    return getPrecioParaTipo(producto, 'mayorista')
  }

  const [depositoId, setDepositoId] = useState<string | null>(null)
  // Si el usuario tiene depósito asignado, usarlo siempre; si no (admin/cajero), usar selección o null (stock total)
  const depositoIdEfectivo = profile?.deposito_id ?? depositoId ?? null

  // Estado del carrito — persiste en sessionStorage para sobrevivir cambios de pestaña
  const [tipoVenta, setTipoVenta] = useState<'minorista' | 'mayorista'>(() => {
    try { return (sessionStorage.getItem('me_tipo') as 'minorista' | 'mayorista') ?? 'minorista' } catch { return 'minorista' }
  })
  const [items, setItems] = useState<CartItem[]>(() => {
    try { return JSON.parse(sessionStorage.getItem('me_items') ?? '[]') } catch { return [] }
  })
  const [busqueda, setBusqueda] = useState('')
  const [metodoPago, setMetodoPago] = useState<MetodoPago>(() => {
    try { return (sessionStorage.getItem('me_metodo') as MetodoPago) ?? 'efectivo' } catch { return 'efectivo' }
  })
  const [montoPagado, setMontoPagado] = useState('')
  const [notas, setNotas] = useState(() => {
    try { return sessionStorage.getItem('me_notas') ?? '' } catch { return '' }
  })

  // Sync a sessionStorage cuando cambian
  useEffect(() => {
    try { sessionStorage.setItem('me_items', JSON.stringify(items)) } catch {}
  }, [items])
  useEffect(() => {
    try { sessionStorage.setItem('me_tipo', tipoVenta) } catch {}
  }, [tipoVenta])
  useEffect(() => {
    try { sessionStorage.setItem('me_metodo', metodoPago) } catch {}
  }, [metodoPago])
  useEffect(() => {
    try { sessionStorage.setItem('me_notas', notas) } catch {}
  }, [notas])

  // Refs de teclado
  const montoRef = useRef<HTMLInputElement>(null)
  const busquedaRef = useRef<HTMLInputElement>(null)

  // Ítem libre
  const [showItemLibre, setShowItemLibre] = useState(false)
  const [itemLibreForm, setItemLibreForm] = useState({ descripcion: '', precio: '', cantidad: '1' })

  // Cliente selector
  const [clienteSeleccionado, setClienteSeleccionado] = useState<Cliente | null>(null)
  const [clienteQuery, setClienteQuery] = useState('')
  const [clienteOpen, setClienteOpen] = useState(false)
  const clienteRef = useRef<HTMLDivElement>(null)

  // Scanner
  const [scannerActivo, setScannerActivo] = useState(false)
  const [scannerMsg, setScannerMsg] = useState('')
  const videoRef = useRef<HTMLVideoElement>(null)
  const handleBarcodeRef = useRef<(code: string) => void>(() => {})

  handleBarcodeRef.current = (codigo: string) => {
    setScannerActivo(false)
    const producto = productos.find(
      p => p.activo && (p.codigo_barras === codigo || p.sku === codigo)
    )
    if (!producto) { toast.error(`Código no encontrado: ${codigo}`); return }
    if (getStockDeposito(producto, depositoIdEfectivo) === 0) { toast.error(`${producto.nombre} sin stock`); return }
    addItem(producto)
    toast.success(`${producto.nombre} agregado`)
  }

  useEffect(() => {
    if (!scannerActivo) return
    if (!('BarcodeDetector' in window)) {
      setScannerMsg('Scanner no disponible — ingresá el código manualmente')
      return
    }
    let cancelled = false
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const detector = new (window as any).BarcodeDetector({
      formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e'],
    })
    setScannerMsg('Apuntá la cámara al código de barras')

    async function startCamera() {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
        const video = videoRef.current
        if (!video) { stream.getTracks().forEach(t => t.stop()); return }
        video.srcObject = stream
        await video.play()
        scan(stream)
      } catch { if (!cancelled) setScannerMsg('No se pudo acceder a la cámara') }
    }

    async function scan(stream: MediaStream) {
      if (cancelled) { stream.getTracks().forEach(t => t.stop()); return }
      const video = videoRef.current
      if (!video || video.readyState < 2) { setTimeout(() => scan(stream), 100); return }
      try {
        const barcodes = await detector.detect(video)
        if (barcodes.length > 0) {
          stream.getTracks().forEach(t => t.stop())
          handleBarcodeRef.current(barcodes[0].rawValue)
          return
        }
      } catch { /* frame no procesable */ }
      setTimeout(() => scan(stream), 200)
    }

    startCamera()
    return () => {
      cancelled = true
      const video = videoRef.current
      if (video?.srcObject) {
        (video.srcObject as MediaStream).getTracks().forEach(t => t.stop())
        video.srcObject = null
      }
    }
  }, [scannerActivo])

  // Cerrar dropdown de cliente al click externo
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (clienteRef.current && !clienteRef.current.contains(e.target as Node)) {
        setClienteOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const productosFiltrados = useMemo(() => {
    const q = busqueda.toLowerCase().trim()
    const activos = productos.filter(p => p.activo)
    if (!q) return activos.slice(0, 40)
    return activos.filter(p =>
      p.nombre.toLowerCase().includes(q) ||
      p.marca?.toLowerCase().includes(q) ||
      p.codigo_barras?.toLowerCase().includes(q) ||
      p.sku?.toLowerCase().includes(q)
    ).slice(0, 40)
  }, [productos, busqueda])

  const clientesFiltrados = useMemo(() => {
    const q = clienteQuery.toLowerCase().trim()
    if (!q) return clientes.slice(0, 8)
    return clientes.filter(c => c.nombre.toLowerCase().includes(q)).slice(0, 8)
  }, [clientes, clienteQuery])

  const metodosPago = useMemo(() => {
    const activos = config?.metodos_pago_activos ?? METODOS_BASE.map(m => m.key)
    const base = METODOS_BASE.filter(m => activos.includes(m.key))
    if (clienteSeleccionado && activos.includes('cuenta_corriente')) {
      return [...base, METODO_CC]
    }
    return base
  }, [clienteSeleccionado, config])

  // Si el método actual queda fuera de los activos al cargar config, resetear al primero disponible
  useEffect(() => {
    if (!config?.metodos_pago_activos?.length) return
    const activos = config.metodos_pago_activos
    if (!activos.includes(metodoPago)) {
      setMetodoPago((activos[0] as MetodoPago) ?? 'efectivo')
    }
  }, [config?.metodos_pago_activos])

  const handleSetTipo = (tipo: 'minorista' | 'mayorista') => {
    setTipoVenta(tipo)
    setItems(prev => prev.map(item => ({
      ...item,
      precioUnitario: applyVolumeRule(item.producto, item.cantidad, tipo),
    })))
  }

  const addItem = (producto: Producto) => {
    if (getStockDeposito(producto, depositoIdEfectivo) === 0) return
    const presentacionId = producto.producto_presentaciones?.[0]?.id ?? ''
    setItems(prev => {
      const existing = prev.find(i => i.producto.id === producto.id)
      if (existing) {
        const newQty = existing.cantidad + 1
        return prev.map(i =>
          i.producto.id === producto.id
            ? { ...i, cantidad: newQty, precioUnitario: applyVolumeRule(producto, newQty, tipoVenta) }
            : i
        )
      }
      const precioUnitario = applyVolumeRule(producto, 1, tipoVenta)
      return [...prev, { producto, presentacionId, precioUnitario, cantidad: 1, descuentoPct: 0 }]
    })
  }

  const updateQty = (productoId: string, delta: number) => {
    setItems(prev =>
      prev
        .map(i => {
          if (i.producto.id !== productoId) return i
          const newQty = i.cantidad + delta
          return { ...i, cantidad: newQty, precioUnitario: applyVolumeRule(i.producto, newQty, tipoVenta) }
        })
        .filter(i => i.cantidad > 0)
    )
  }

  const setDescuento = (productoId: string, pct: number) => {
    setItems(prev =>
      prev.map(i => i.producto.id === productoId
        ? { ...i, descuentoPct: Math.min(100, Math.max(0, isNaN(pct) ? 0 : pct)) }
        : i
      )
    )
  }

  const removeItem = (productoId: string) => {
    setItems(prev => prev.filter(i => i.producto.id !== productoId))
  }

  const seleccionarCliente = (c: Cliente) => {
    setClienteSeleccionado(c)
    setClienteQuery('')
    setClienteOpen(false)
    // Actualizar tipo de venta al tipo del cliente
    if (c.tipo !== tipoVenta) handleSetTipo(c.tipo)
  }

  const quitarCliente = () => {
    setClienteSeleccionado(null)
    setClienteQuery('')
    if (metodoPago === 'cuenta_corriente') setMetodoPago('efectivo')
  }

  const total = getCartTotal(items)
  const subtotalBruto = items.reduce((s, i) => s + i.precioUnitario * i.cantidad, 0)
  const descuentoTotal = subtotalBruto - total
  const montoPagadoNum = parseFloat(montoPagado) || 0
  const vuelto = metodoPago === 'efectivo' ? Math.max(0, montoPagadoNum - total) : 0
  const montoPendiente = metodoPago === 'efectivo' && montoPagadoNum > 0 ? total - montoPagadoNum : 0
  const totalUnidades = items.reduce((s, i) => s + i.cantidad, 0)

  const excedeLimiteCredito =
    metodoPago === 'cuenta_corriente' &&
    !!clienteSeleccionado &&
    (clienteSeleccionado.limite_credito ?? 0) > 0 &&
    (clienteSeleccionado.saldo_actual + total) > (clienteSeleccionado.limite_credito ?? 0)

  const itemsSinStockSuficiente = items.filter(
    i => !i.esLibre && i.cantidad > getStockDeposito(i.producto, depositoIdEfectivo)
  )

  const puedeConfirmar =
    items.length > 0 &&
    !!depositoIdEfectivo &&
    itemsSinStockSuficiente.length === 0 &&
    !excedeLimiteCredito &&
    (metodoPago !== 'efectivo' || montoPagadoNum >= total) &&
    (metodoPago !== 'cuenta_corriente' || !!clienteSeleccionado)

  const handleConfirmar = () => {
    if (!profile?.id || !depositoIdEfectivo) return
    confirmarVenta.mutate(
      {
        tipo: tipoVenta,
        items,
        metodoPago,
        montoPagado: metodoPago === 'efectivo' ? montoPagadoNum : total,
        clienteId: clienteSeleccionado?.id ?? null,
        usuarioId: profile.id,
        depositoId: depositoIdEfectivo!,
        notas: notas.trim() || undefined,
        cajaId: cajaActual?.id ?? null,
        clienteSaldoActual: clienteSeleccionado?.saldo_actual,
        clienteLimiteCredito: clienteSeleccionado?.limite_credito ?? undefined,
      },
      {
        onSuccess: ({ numero, ventaId }) => {
          // Construir objeto para el ticket con los datos del carrito actual
          const now = new Date().toISOString()
          const montoEfectivo = metodoPago === 'efectivo' ? Math.min(montoPagadoNum, total) : total
          const ventaObj: Venta = {
            id: ventaId,
            numero,
            tipo: tipoVenta,
            estado: 'pagada',
            subtotal: subtotalBruto,
            descuento_total: descuentoTotal,
            total,
            total_pagado: total,
            notas: notas.trim() || null,
            usuario_id: profile?.id ?? '',
            cliente_id: clienteSeleccionado?.id ?? null,
            created_at: now,
            updated_at: now,
            deleted_at: null,
            clientes: clienteSeleccionado
              ? { id: clienteSeleccionado.id, nombre: clienteSeleccionado.nombre, saldo_actual: clienteSeleccionado.saldo_actual }
              : undefined,
            pagos: [{ id: '', metodo: metodoPago, monto: montoEfectivo, venta_id: ventaId, usuario_id: profile?.id ?? '', created_at: now }],
            venta_items: items.map((item, idx) => ({
              id: `tmp-${idx}`,
              venta_id: ventaId,
              producto_id: item.producto.id,
              presentacion_id: item.presentacionId,
              cantidad: item.cantidad,
              precio_unitario: item.precioUnitario,
              descuento: item.descuentoPct,
              subtotal: item.precioUnitario * item.cantidad * (1 - item.descuentoPct / 100),
              created_at: now,
              productos: { nombre: item.producto.nombre, marca: item.producto.marca ?? undefined },
            })),
          } as unknown as Venta

          setVentaCompletada({ numero, ventaObj, clienteEmail: clienteSeleccionado?.email ?? null })
          setItems([])
          setBusqueda('')
          setMontoPagado('')
          setNotas('')
          setMetodoPago('efectivo')
          setClienteSeleccionado(null)
          setClienteQuery('')
          try {
            sessionStorage.removeItem('me_items')
            sessionStorage.removeItem('me_tipo')
            sessionStorage.removeItem('me_metodo')
            sessionStorage.removeItem('me_notas')
          } catch {}
          // Auto-imprimir ticket — no bloquea si config aún no cargó
          if (config) printTicket(ventaObj, config).catch(() => {})
        },
        onError: err => {
          toast.error(err instanceof Error ? err.message : 'Error al registrar la venta')
        },
      }
    )
  }

  // Atajos globales: F1-F5 para métodos de pago, F6 para ítem libre
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'F6') {
        e.preventDefault()
        setShowItemLibre(v => !v)
        return
      }
      if (!(e.key in METODO_KBD)) return
      if (items.length === 0) return
      e.preventDefault()
      const metodo = METODO_KBD[e.key]
      if (!metodosPago.find(m => m.key === metodo)) return
      setMetodoPago(metodo)
      if (metodo === 'efectivo') setTimeout(() => montoRef.current?.focus(), 60)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [items.length, metodosPago])

  function handleAgregarItemLibre() {
    const desc = itemLibreForm.descripcion.trim()
    const precio = parseFloat(itemLibreForm.precio)
    const cantidad = parseInt(itemLibreForm.cantidad) || 1
    if (!desc) { toast.error('Ingresá una descripción'); return }
    if (!precio || precio <= 0) { toast.error('Ingresá un precio válido'); return }
    const fakeProducto: Producto = {
      id: `libre-${Date.now()}`,
      nombre: desc,
      marca: null, codigo_barras: null, sku: null, categoria_id: null,
      foto_url: null, stock_minimo: 0, activo: true,
      deleted_at: null, created_at: '', updated_at: '',
      stock: [],
    }
    setItems(prev => [...prev, {
      producto: fakeProducto,
      presentacionId: '',
      precioUnitario: precio,
      cantidad,
      descuentoPct: 0,
      esLibre: true,
      descripcionLibre: desc,
    }])
    setItemLibreForm({ descripcion: '', precio: '', cantidad: '1' })
    setShowItemLibre(false)
    busquedaRef.current?.focus()
  }

  async function handleDescargarTicket() {
    if (!ventaCompletada || !config) return
    setDescargandoPDF(true)
    try {
      const blob = await pdf(<TicketDocument venta={ventaCompletada.ventaObj} config={config} />).toBlob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `ticket-${ventaCompletada.numero}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error al generar el ticket')
    } finally {
      setDescargandoPDF(false)
    }
  }

  return (
    <div style={embedded ? undefined : { padding: 'var(--me-space-8)' }}>

      {/* Banner post-venta */}
      {ventaCompletada && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 16px', marginBottom: 'var(--me-space-5)',
          background: 'var(--me-ok-soft)', borderRadius: 10,
          border: '1px solid rgba(34,197,94,0.25)',
        }}>
          <CheckIcon size={16} style={{ color: 'var(--me-ok)', flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 13.5, fontWeight: 600, color: 'var(--me-ok)' }}>
            Venta #{ventaCompletada.numero} registrada · Ticket enviado a imprimir
          </span>
          <button
            onClick={() => config && printTicket(ventaCompletada.ventaObj, config).catch(() => toast.error('Error al imprimir'))}
            disabled={!config}
            className="me-btn me-btn-ghost"
            style={{ fontSize: 12.5, padding: '5px 12px', height: 32, display: 'flex', alignItems: 'center', gap: 5 }}
          >
            <PrintIcon size={13} />
            Reimprimir
          </button>
          <button
            onClick={handleDescargarTicket}
            disabled={descargandoPDF || !config}
            className="me-btn me-btn-ghost"
            style={{ fontSize: 12.5, padding: '5px 12px', height: 32 }}
          >
            {descargandoPDF ? '...' : 'PDF'}
          </button>
          {ventaCompletada.clienteEmail && (
            <button
              onClick={() => {
                enviarRecibo.mutate(ventaCompletada.ventaObj.id, {
                  onSuccess: () => toast.success('Recibo enviado a ' + ventaCompletada.clienteEmail),
                  onError: () => toast.error('No se pudo enviar el recibo'),
                })
              }}
              disabled={enviarRecibo.isPending}
              className="me-btn me-btn-ghost"
              style={{ fontSize: 12.5, padding: '5px 12px', height: 32, display: 'flex', alignItems: 'center', gap: 5 }}
            >
              <MailIcon size={13} />
              {enviarRecibo.isPending ? 'Enviando...' : 'Enviar recibo'}
            </button>
          )}
          <button
            onClick={() => setVentaCompletada(null)}
            className="me-icon-btn"
            style={{ width: 28, height: 28, flexShrink: 0 }}
          >
            <CloseIcon size={13} />
          </button>
        </div>
      )}

      {/* Aviso caja cerrada */}
      {!cajaActual && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 14px', marginBottom: 'var(--me-space-5)',
          background: 'var(--me-warn-soft)', borderRadius: 10,
          border: '1px solid rgba(245,158,11,0.25)', fontSize: 12.5, color: 'var(--me-warn)',
        }}>
          <AlertTriangleIcon size={14} style={{ flexShrink: 0 }} />
          No hay turno de caja abierto. La venta se registrará sin movimiento de caja.
        </div>
      )}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 'var(--me-space-6)', gap: 12, flexWrap: 'wrap' }}>
        <h1 className="me-page-title">Nueva venta</h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Selector de depósito — visible si hay 2+ y el usuario no tiene uno fijo */}
          {depositos.length > 1 && !profile?.deposito_id && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <WarehouseIcon size={14} style={{ color: 'var(--me-text-3)', flexShrink: 0 }} />
              <select
                value={depositoIdEfectivo ?? ''}
                onChange={e => setDepositoId(e.target.value || null)}
                style={{
                  padding: '5px 10px', borderRadius: 8,
                  border: `1.5px solid ${!depositoIdEfectivo ? 'var(--me-bad)' : 'var(--me-line)'}`,
                  background: 'var(--me-surface)',
                  fontFamily: 'var(--me-sans)', fontSize: 12.5,
                  color: 'var(--me-text-2)', cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="">— Seleccioná depósito para vender —</option>
                {depositos.map(d => (
                  <option key={d.id} value={d.id}>{d.nombre}</option>
                ))}
              </select>
            </div>
          )}

          {/* Tipo toggle */}
        <div style={{
          display: 'flex',
          background: 'var(--me-surface-2)',
          border: '1px solid var(--me-line)',
          borderRadius: 10,
          padding: 3,
          gap: 2,
        }}>
          {(['minorista', 'mayorista'] as const).map(tipo => (
            <button
              key={tipo}
              onClick={() => handleSetTipo(tipo)}
              style={{
                padding: '6px 20px',
                borderRadius: 8,
                border: 'none',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                fontFamily: 'var(--me-sans)',
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
                background: tipoVenta === tipo
                  ? tipo === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-acid)'
                  : 'transparent',
                color: tipoVenta === tipo
                  ? tipo === 'mayorista' ? '#ffffff' : 'var(--me-acid-ink)'
                  : 'var(--me-text-3)',
              }}
            >
              {tipo === 'minorista' ? 'Minorista' : 'Mayorista'}
            </button>
          ))}
        </div>
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: 'var(--me-space-6)', alignItems: 'flex-start' }}>

        {/* LEFT: product search + list */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 'var(--me-space-4)' }}>
            <div className="me-input" style={{ flex: 1 }}>
              <SearchIcon size={16} style={{ color: 'var(--me-text-3)', flexShrink: 0 }} />
              <input
                ref={busquedaRef}
                type="text"
                placeholder="Buscar por nombre, marca, código de barras…  [Enter] agrega"
                value={busqueda}
                onChange={e => setBusqueda(e.target.value)}
                autoFocus
                autoComplete="off"
                onKeyDown={e => {
                  if (e.key !== 'Enter') return
                  const q = busqueda.trim()
                  if (!q) return
                  const exacto = productos.find(
                    p => p.activo && (p.codigo_barras === q || p.sku === q)
                  )
                  if (exacto) {
                    if (getStockDeposito(exacto, depositoIdEfectivo) === 0) {
                      toast.error(`${exacto.nombre} sin stock`)
                    } else {
                      addItem(exacto)
                      setBusqueda('')
                      toast.success(`${exacto.nombre} agregado`)
                    }
                    return
                  }
                  const activos = productosFiltrados.filter(p => getStockDeposito(p, depositoIdEfectivo) > 0)
                  if (activos.length === 1) {
                    addItem(activos[0])
                    setBusqueda('')
                    toast.success(`${activos[0].nombre} agregado`)
                  } else if (productosFiltrados.length === 0) {
                    toast.error('Sin resultados')
                  }
                }}
              />
              {busqueda && (
                <button
                  type="button"
                  onClick={() => setBusqueda('')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--me-text-3)', display: 'flex', alignItems: 'center', padding: 0 }}
                >
                  <CloseIcon size={14} />
                </button>
              )}
            </div>
            <button
              type="button"
              onClick={() => setScannerActivo(true)}
              title="Escanear código de barras"
              style={{
                width: 44, height: 44, flexShrink: 0,
                border: '1.5px solid var(--me-line)', borderRadius: 10,
                background: 'var(--me-surface)', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--me-text-3)',
                transition: 'background var(--me-dur-short), color var(--me-dur-short)',
              }}
            >
              <CameraIcon size={18} />
            </button>
          </div>

          <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
            {isLoading ? (
              <div style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--me-text-3)', fontSize: 13.5 }}>
                Cargando productos...
              </div>
            ) : productosFiltrados.length === 0 && busqueda ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <SearchIcon size={28} style={{ color: 'var(--me-text-4)', marginBottom: 10 }} />
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--me-text-3)' }}>
                  Sin resultados para <strong>"{busqueda}"</strong>
                </p>
                <p style={{ margin: '4px 0 0', fontSize: 12.5, color: 'var(--me-text-4)' }}>
                  Probá con el nombre completo o código de barras
                </p>
              </div>
            ) : productosFiltrados.length === 0 ? (
              <div style={{ padding: '48px 24px', textAlign: 'center' }}>
                <BoxIcon size={28} style={{ color: 'var(--me-text-4)', marginBottom: 10 }} />
                <p style={{ margin: 0, fontSize: 13.5, color: 'var(--me-text-3)' }}>
                  Sin productos activos — creá uno en la sección Productos
                </p>
              </div>
            ) : (
              productosFiltrados.map((producto, i) => {
                const stock = getStockDeposito(producto, depositoIdEfectivo)
                const precio = getPrecioParaTipo(producto, tipoVenta)
                const enCarrito = items.find(it => it.producto.id === producto.id)
                const sinStock = stock === 0

                return (
                  <div
                    key={producto.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 'var(--me-space-4)',
                      padding: '10px var(--me-space-4)',
                      borderBottom: i < productosFiltrados.length - 1 ? '1px solid var(--me-line-2)' : 'none',
                      background: i % 2 === 1 ? 'var(--me-surface-2)' : 'transparent',
                    }}
                  >
                    {producto.foto_url ? (
                      <img
                        src={producto.foto_url}
                        alt=""
                        style={{
                          width: 36, height: 36, objectFit: 'cover',
                          borderRadius: 6, flexShrink: 0,
                          border: '1px solid var(--me-line-2)',
                          opacity: sinStock ? 0.4 : 1,
                        }}
                      />
                    ) : (
                      <div style={{
                        width: 36, height: 36, borderRadius: 6, flexShrink: 0,
                        background: 'var(--me-surface-3)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <BoxIcon size={16} style={{ color: 'var(--me-text-4)' }} />
                      </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0, opacity: sinStock ? 0.5 : 1 }}>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: 2 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--me-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {producto.nombre}
                        </span>
                        {producto.marca && (
                          <span style={{ fontSize: 11.5, color: 'var(--me-text-4)', flexShrink: 0 }}>{producto.marca}</span>
                        )}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {sinStock ? (
                          <span style={{ fontSize: 11, color: 'var(--me-bad)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 3 }}>
                            <AlertTriangleIcon size={11} strokeWidth={2} /> Sin stock
                          </span>
                        ) : (
                          <span style={{ fontSize: 11.5, color: 'var(--me-text-3)' }}>
                            Stock: <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}>{stock}</span>
                          </span>
                        )}
                        {producto.codigo_barras && (
                          <span style={{ fontSize: 11, color: 'var(--me-text-4)', fontFamily: 'var(--me-mono)' }}>
                            {producto.codigo_barras}
                          </span>
                        )}
                      </div>
                    </div>

                    <div style={{ flexShrink: 0, minWidth: 80, textAlign: 'right' }}>
                      <span style={{
                        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                        fontSize: 14, fontWeight: 700,
                        color: tipoVenta === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)',
                      }}>
                        {formatMoney(precio)}
                      </span>
                    </div>

                    <div style={{ flexShrink: 0, width: 108 }}>
                      {enCarrito ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'flex-end' }}>
                          <button onClick={() => updateQty(producto.id, -1)} className="me-icon-btn" style={{ width: 28, height: 28, borderRadius: 6 }}>
                            <MinusIcon size={13} />
                          </button>
                          <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 14, fontWeight: 700, color: 'var(--me-text)', minWidth: 22, textAlign: 'center' }}>
                            {enCarrito.cantidad}
                          </span>
                          <button onClick={() => updateQty(producto.id, 1)} className="me-icon-btn" style={{ width: 28, height: 28, borderRadius: 6 }}>
                            <PlusIcon size={13} />
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => addItem(producto)}
                          className="me-btn me-btn-ghost"
                          style={{ fontSize: 12, padding: '5px 10px', height: 30, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                        >
                          <PlusIcon size={12} strokeWidth={2} /> Agregar
                        </button>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>

        {/* RIGHT: cart + payment */}
        <div style={{ width: 360, flexShrink: 0, position: 'sticky', top: 32 }}>
          <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>

            {/* Cart header */}
            <div style={{
              padding: '14px 20px', borderBottom: '1px solid var(--me-line-2)',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <h2 className="me-section-title" style={{ margin: 0 }}>Carrito</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {totalUnidades > 0 && (
                  <span style={{ fontFamily: 'var(--me-mono)', fontSize: 12, color: 'var(--me-text-3)', fontFeatureSettings: '"tnum"' }}>
                    {totalUnidades} unid.
                  </span>
                )}
                <button
                  onClick={() => setShowItemLibre(v => !v)}
                  title="Agregar ítem sin producto [F6]"
                  style={{
                    display: 'flex', alignItems: 'center', gap: 4,
                    background: showItemLibre ? 'var(--me-acid-soft)' : 'none',
                    border: `1px solid ${showItemLibre ? 'var(--me-acid)' : 'var(--me-line)'}`,
                    borderRadius: 6, cursor: 'pointer', padding: '3px 8px',
                    fontSize: 11.5, color: showItemLibre ? '#4a5800' : 'var(--me-text-3)',
                    fontFamily: 'var(--me-sans)', fontWeight: 600,
                    transition: 'all var(--me-dur-short)',
                  }}
                >
                  <PlusIcon size={11} strokeWidth={2.5} />
                  Ítem libre
                  <span style={{ fontSize: 9, fontFamily: 'var(--me-mono)', opacity: 0.6, background: 'var(--me-surface-3)', borderRadius: 3, padding: '1px 4px' }}>F6</span>
                </button>
                {items.length > 0 && (
                  <button
                    onClick={() => setItems([])}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--me-bad)', fontFamily: 'var(--me-sans)', padding: 0, fontWeight: 600 }}
                  >
                    Vaciar
                  </button>
                )}
              </div>
            </div>

            {/* Formulario ítem libre */}
            {showItemLibre && (
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--me-line-2)', background: 'var(--me-acid-soft)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11.5, fontWeight: 600, color: '#4a5800', marginBottom: 2 }}>Ítem libre — no descuenta stock</div>
                <input
                  className="me-input"
                  style={{ width: '100%' }}
                  type="text"
                  placeholder="Descripción del ítem"
                  value={itemLibreForm.descripcion}
                  onChange={e => setItemLibreForm(f => ({ ...f, descripcion: e.target.value }))}
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleAgregarItemLibre() }}
                />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 6 }}>
                  <div className="me-input">
                    <span style={{ fontSize: 13, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)', flexShrink: 0 }}>$</span>
                    <input
                      type="number" min="0" step="0.01" placeholder="Precio"
                      value={itemLibreForm.precio}
                      onChange={e => setItemLibreForm(f => ({ ...f, precio: e.target.value }))}
                      style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}
                      onKeyDown={e => { if (e.key === 'Enter') handleAgregarItemLibre() }}
                    />
                  </div>
                  <input
                    className="me-input"
                    type="number" min="1" placeholder="Cant."
                    value={itemLibreForm.cantidad}
                    onChange={e => setItemLibreForm(f => ({ ...f, cantidad: e.target.value }))}
                    style={{ fontFamily: 'var(--me-mono)', textAlign: 'center' }}
                    onKeyDown={e => { if (e.key === 'Enter') handleAgregarItemLibre() }}
                  />
                </div>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button type="button" className="me-btn me-btn-ghost" style={{ flex: 1, fontSize: 12 }}
                    onClick={() => { setShowItemLibre(false); setItemLibreForm({ descripcion: '', precio: '', cantidad: '1' }) }}>
                    Cancelar
                  </button>
                  <button type="button" className="me-btn me-btn-primary" style={{ flex: 2, fontSize: 12 }}
                    onClick={handleAgregarItemLibre}>
                    Agregar al carrito
                  </button>
                </div>
              </div>
            )}

            {items.length === 0 && (
              <div style={{ padding: '36px 20px', textAlign: 'center' }}>
                <BoxIcon size={28} style={{ color: 'var(--me-text-4)', marginBottom: 8 }} />
                <p style={{ margin: 0, fontSize: 13, color: 'var(--me-text-3)' }}>
                  Agregá productos al carrito para comenzar una venta
                </p>
              </div>
            )}

            {items.length > 0 && (
              <>
                {/* Items */}
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {items.map(item => {
                    const lineaTotal = item.precioUnitario * item.cantidad * (1 - item.descuentoPct / 100)
                    const stockDisponible = getStockDeposito(item.producto, depositoIdEfectivo)
                    const sobreStock = item.cantidad > stockDisponible
                    return (
                      <div
                        key={item.producto.id}
                        style={{
                          padding: '9px 16px', borderBottom: '1px solid var(--me-line-2)',
                          background: sobreStock ? 'var(--me-bad-soft)' : undefined,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 1 }}>
                              {item.esLibre && (
                                <span style={{ fontSize: 9, fontWeight: 700, background: 'var(--me-warn-soft)', color: 'var(--me-warn)', borderRadius: 3, padding: '1px 4px', flexShrink: 0 }}>LIBRE</span>
                              )}
                              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--me-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {item.descripcionLibre ?? item.producto.nombre}
                              </span>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 11.5, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}>
                                {formatMoney(item.precioUnitario)} × {item.cantidad}
                              </span>
                              {sobreStock && (
                                <span style={{ fontSize: 11, color: 'var(--me-bad)', fontWeight: 600 }}>
                                  stock: {stockDisponible}
                                </span>
                              )}
                            </div>
                          </div>
                          <div style={{
                            fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                            fontSize: 13, fontWeight: 700, flexShrink: 0,
                            color: tipoVenta === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)',
                          }}>
                            {formatMoney(lineaTotal)}
                          </div>
                          <button
                            onClick={() => removeItem(item.producto.id)}
                            className="me-icon-btn"
                            style={{ width: 24, height: 24, borderRadius: 4, flexShrink: 0 }}
                          >
                            <CloseIcon size={12} />
                          </button>
                        </div>

                        {/* Discount row */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
                          <span style={{ fontSize: 11, color: 'var(--me-text-4)' }}>Desc.:</span>
                          <div style={{ display: 'flex', alignItems: 'center', border: '1px solid var(--me-line)', borderRadius: 5, overflow: 'hidden', height: 20 }}>
                            <input
                              type="number"
                              value={item.descuentoPct || ''}
                              onChange={e => setDescuento(item.producto.id, parseFloat(e.target.value))}
                              placeholder="0"
                              min="0"
                              max="100"
                              style={{
                                width: 36, padding: '0 4px', border: 'none', outline: 'none',
                                fontFamily: 'var(--me-mono)', fontSize: 11,
                                color: 'var(--me-text-2)', background: 'transparent',
                                fontFeatureSettings: '"tnum"',
                              }}
                            />
                            <span style={{ fontSize: 11, color: 'var(--me-text-3)', paddingRight: 4 }}>%</span>
                          </div>
                          {item.descuentoPct > 0 && (
                            <span style={{ fontSize: 11, color: 'var(--me-ok)', fontFamily: 'var(--me-mono)' }}>
                              -{formatMoney(item.precioUnitario * item.cantidad * (item.descuentoPct / 100))}
                            </span>
                          )}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* Total */}
                <div style={{
                  padding: '12px 20px',
                  background: 'var(--me-surface-2)',
                  borderTop: '2px solid var(--me-line)',
                }}>
                  {descuentoTotal > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ fontSize: 12, color: 'var(--me-text-3)' }}>Subtotal</span>
                      <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 12, color: 'var(--me-text-3)' }}>
                        {formatMoney(subtotalBruto)}
                      </span>
                    </div>
                  )}
                  {descuentoTotal > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: 12, color: 'var(--me-ok)' }}>Descuento</span>
                      <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 12, color: 'var(--me-ok)', fontWeight: 600 }}>
                        -{formatMoney(descuentoTotal)}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="me-label">Total</span>
                    <span style={{
                      fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                      fontSize: 22, fontWeight: 800,
                      color: tipoVenta === 'mayorista' ? 'var(--me-mayor)' : 'var(--me-text)',
                    }}>
                      {formatMoney(total)}
                    </span>
                  </div>
                </div>

                {/* Payment section */}
                <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>

                  {/* Cliente selector */}
                  <div ref={clienteRef} style={{ position: 'relative' }}>
                    <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Cliente</label>
                    {clienteSeleccionado ? (
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '7px 10px', borderRadius: 8,
                        border: '1.5px solid var(--me-acid)',
                        background: 'var(--me-acid-soft)',
                      }}>
                        <UsersIcon size={14} style={{ color: '#4a5800', flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: '#3a4800', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {clienteSeleccionado.nombre}
                          </div>
                          <div style={{ fontSize: 11, color: '#4a5800', fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}>
                            Saldo: {formatMoney(clienteSeleccionado.saldo_actual)}
                          </div>
                        </div>
                        <button
                          onClick={quitarCliente}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#4a5800', display: 'flex', alignItems: 'center', padding: 2 }}
                        >
                          <CloseIcon size={13} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="me-input">
                          <UsersIcon size={14} style={{ color: 'var(--me-text-3)', flexShrink: 0 }} />
                          <input
                            type="text"
                            placeholder="Buscar cliente (opcional)..."
                            value={clienteQuery}
                            onChange={e => { setClienteQuery(e.target.value); setClienteOpen(true) }}
                            onFocus={() => setClienteOpen(true)}
                            autoComplete="off"
                          />
                        </div>
                        {clienteOpen && clientesFiltrados.length > 0 && (
                          <div style={{
                            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                            background: 'var(--me-surface)', border: '1px solid var(--me-line)',
                            borderRadius: 8, marginTop: 4,
                            boxShadow: 'var(--me-shadow-popover)',
                            overflow: 'hidden',
                          }}>
                            {clientesFiltrados.map(c => (
                              <button
                                key={c.id}
                                onMouseDown={() => seleccionarCliente(c)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  width: '100%', padding: '8px 12px',
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  borderBottom: '1px solid var(--me-line-2)',
                                  textAlign: 'left',
                                  transition: 'background var(--me-dur-short)',
                                }}
                                onMouseEnter={e => (e.currentTarget.style.background = 'var(--me-surface-2)')}
                                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                              >
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--me-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {c.nombre}
                                  </div>
                                  <div style={{ fontSize: 11, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)' }}>
                                    Saldo: {formatMoney(c.saldo_actual)}
                                  </div>
                                </div>
                                {c.tipo === 'mayorista' && <span className="me-pill me-pill-mayor" style={{ fontSize: 10 }}>Mayor.</span>}
                              </button>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>

                  {/* Método de pago */}
                  <div>
                    <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Método de pago</label>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                      {metodosPago.map(m => {
                        const activo = metodoPago === m.key
                        return (
                          <button
                            key={m.key}
                            onClick={() => {
                              setMetodoPago(m.key)
                              if (m.key === 'efectivo') setTimeout(() => montoRef.current?.focus(), 60)
                            }}
                            style={{
                              padding: '7px 6px', borderRadius: 8, border: '1.5px solid',
                              borderColor: activo ? 'var(--me-acid)' : 'var(--me-line)',
                              background: activo ? 'var(--me-acid-soft)' : 'transparent',
                              color: activo ? '#4a5800' : 'var(--me-text-2)',
                              fontSize: 12, fontWeight: 600, cursor: 'pointer',
                              fontFamily: 'var(--me-sans)',
                              transition: 'border-color var(--me-dur-short), background var(--me-dur-short), color var(--me-dur-short)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                            }}
                          >
                            {m.label}
                            <span style={{
                              fontSize: 9, fontFamily: 'var(--me-mono)',
                              opacity: 0.55, background: activo ? 'rgba(0,0,0,0.08)' : 'var(--me-surface-3)',
                              borderRadius: 3, padding: '1px 4px',
                            }}>
                              {(m as typeof m & { kbd?: string }).kbd}
                            </span>
                          </button>
                        )
                      })}
                    </div>

                    {/* Datos bancarios para transferencia */}
                    {metodoPago === 'transferencia' && (
                      config?.qr_transferencia_url || config?.alias_transferencia || config?.cbu
                    ) && (
                      <div style={{
                        marginTop: 8, padding: '10px 12px', borderRadius: 8,
                        background: 'var(--me-surface-2)', border: '1px solid var(--me-line-2)',
                        fontSize: 12,
                      }}>
                        <div style={{ fontWeight: 600, color: 'var(--me-text-2)', marginBottom: 8 }}>
                          Datos para transferencia
                        </div>
                        {config?.qr_transferencia_url && (
                          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}>
                            <img
                              src={config.qr_transferencia_url}
                              alt="QR de pago"
                              style={{ width: 140, height: 140, objectFit: 'contain', borderRadius: 8, background: '#fff', border: '1px solid var(--me-line-2)' }}
                            />
                          </div>
                        )}
                        {config?.alias_transferencia && (
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ color: 'var(--me-text-3)' }}>Alias</span>
                            <span style={{ fontFamily: 'var(--me-mono)', fontWeight: 600, color: 'var(--me-text)' }}>
                              {config.alias_transferencia}
                            </span>
                          </div>
                        )}
                        {config?.cbu && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: 'var(--me-text-3)' }}>CBU</span>
                            <span style={{ fontFamily: 'var(--me-mono)', fontSize: 11, fontWeight: 600, color: 'var(--me-text)' }}>
                              {config.cbu}
                            </span>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Avisos cuenta corriente */}
                    {metodoPago === 'cuenta_corriente' && clienteSeleccionado && (
                      excedeLimiteCredito ? (
                        <div style={{ marginTop: 8, padding: '7px 10px', background: 'var(--me-bad-soft)', borderRadius: 8, fontSize: 12, color: 'var(--me-bad)', fontWeight: 600 }}>
                          Límite de crédito superado — disponible: {formatMoney((clienteSeleccionado.limite_credito ?? 0) - clienteSeleccionado.saldo_actual)}
                        </div>
                      ) : (
                        <div style={{ marginTop: 8, padding: '7px 10px', background: 'var(--me-warn-soft)', borderRadius: 8, fontSize: 12, color: 'var(--me-warn)' }}>
                          Se cargará {formatMoney(total)} a la cuenta de {clienteSeleccionado.nombre}
                          {' '}(saldo actual: {formatMoney(clienteSeleccionado.saldo_actual)})
                        </div>
                      )
                    )}
                  </div>

                  {/* Monto recibido — solo efectivo */}
                  {metodoPago === 'efectivo' && (
                    <div>
                      <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Monto recibido</label>
                      <div className="me-input">
                        <span style={{ fontSize: 14, color: 'var(--me-text-3)', fontFamily: 'var(--me-mono)', flexShrink: 0 }}>$</span>
                        <input
                          ref={montoRef}
                          type="number"
                          placeholder="0"
                          value={montoPagado}
                          onChange={e => setMontoPagado(e.target.value)}
                          min="0"
                          step="any"
                          style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"' }}
                          onKeyDown={e => {
                            if (e.key === 'Enter' && puedeConfirmar && !confirmarVenta.isPending) {
                              handleConfirmar()
                            }
                          }}
                        />
                      </div>
                      {montoPendiente > 0 && (
                        <div style={{ marginTop: 6, fontSize: 11.5, color: 'var(--me-bad)', fontWeight: 600 }}>
                          Faltan {formatMoney(montoPendiente)}
                        </div>
                      )}
                      {montoPagadoNum >= total && montoPagadoNum > 0 && (
                        <div style={{
                          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                          marginTop: 8, padding: '8px 12px',
                          background: 'var(--me-ok-soft)', borderRadius: 8,
                        }}>
                          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--me-ok)' }}>Vuelto</span>
                          <span style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontSize: 14, fontWeight: 700, color: 'var(--me-ok)' }}>
                            {formatMoney(vuelto)}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Notas */}
                  <div>
                    <label className="me-label" style={{ display: 'block', marginBottom: 6 }}>Notas</label>
                    <textarea
                      value={notas}
                      onChange={e => setNotas(e.target.value)}
                      placeholder="Observaciones..."
                      rows={2}
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: 8,
                        border: '1.5px solid var(--me-line)', background: 'var(--me-surface)',
                        fontFamily: 'var(--me-sans)', fontSize: 13, color: 'var(--me-text)',
                        resize: 'none', outline: 'none', boxSizing: 'border-box',
                      }}
                    />
                  </div>
                </div>

                {/* Confirm */}
                <div style={{ padding: '0 20px 20px' }}>
                  <button
                    onClick={handleConfirmar}
                    disabled={!puedeConfirmar || confirmarVenta.isPending}
                    className="me-btn me-btn-primary"
                    style={{
                      width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 6,
                      fontSize: 14,
                      opacity: puedeConfirmar && !confirmarVenta.isPending ? 1 : 0.45,
                      cursor: puedeConfirmar && !confirmarVenta.isPending ? 'pointer' : 'not-allowed',
                    }}
                  >
                    <CheckIcon size={15} />
                    {confirmarVenta.isPending ? 'Registrando...' : `Confirmar · ${formatMoney(total)}`}
                    {!confirmarVenta.isPending && puedeConfirmar && (
                      <span style={{ fontSize: 9, fontFamily: 'var(--me-mono)', opacity: 0.65, background: 'rgba(0,0,0,0.12)', borderRadius: 3, padding: '1px 4px', marginLeft: 2 }}>Enter</span>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Scanner modal */}
      {scannerActivo && (
        <div
          onClick={() => setScannerActivo(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 60,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 20,
          }}
        >
          <div onClick={e => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
            <div style={{ position: 'relative', width: 300, height: 300, borderRadius: 12, overflow: 'hidden', background: '#000' }}>
              <video ref={videoRef} playsInline muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                width: 220, height: 130,
                border: '2px solid var(--me-acid)', borderRadius: 6,
              }} />
            </div>
            <p style={{ margin: 0, fontSize: 13.5, fontFamily: 'var(--me-sans)', color: 'rgba(255,255,255,0.8)', textAlign: 'center' }}>
              {scannerMsg}
            </p>
            <button
              onClick={() => setScannerActivo(false)}
              className="me-btn me-btn-ghost"
              style={{ borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}
            >
              Cancelar
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
